import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit'
import { remindersApi } from '@/api/remindersApi'
import { normalizeReminder } from '@/features/reminders/reminderTime'
import type { Reminder } from '@/features/reminders/types'
import type { SnowflakeId } from '@/types/snowflake'

interface RemindersState {
  items: Reminder[]
  loading: boolean
  loaded: boolean
  error: string | null
}

const initialState: RemindersState = { items: [], loading: false, loaded: false, error: null }

export const fetchReminders = createAsyncThunk('reminders/fetch', async () => {
  // axios interceptor unwraps { code, data } -> res is the inner payload
  // page=-1 + pageSize=0 = 后端不分页模式,个人提醒全量拉取(原 pageSize:500 会静默截断)
  const res = await remindersApi.search({ page: -1, pageSize: 0, orderBy: 'dueDate,dueTime' })
  return (res?.results ?? []).map(normalizeReminder)
})

const remindersSlice = createSlice({
  name: 'reminders',
  initialState,
  reducers: {
    setReminders: (state, action: PayloadAction<Reminder[]>) => {
      state.items = action.payload
    },
    upsertReminder: (state, action: PayloadAction<Reminder>) => {
      const i = state.items.findIndex((x) => x.id === action.payload.id)
      if (i >= 0) state.items[i] = action.payload
      else state.items.unshift(action.payload)
    },
    removeReminder: (state, action: PayloadAction<SnowflakeId>) => {
      state.items = state.items.filter((x) => x.id !== action.payload)
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReminders.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchReminders.fulfilled, (state, action) => {
        state.loading = false
        state.loaded = true
        state.items = action.payload
      })
      .addCase(fetchReminders.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to load reminders'
      })
  },
})

export const { setReminders, upsertReminder, removeReminder } = remindersSlice.actions
export default remindersSlice.reducer
